import { useEffect, Component, type ReactNode } from "react";
import { BrowserRouter, useLocation, useSearchParams } from "react-router-dom";
import { AppRoutes } from "./router/index";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { nhost } from "./lib/nhost";
import RouteRedirects from "@/components/feature/RouteRedirects";
import { WhatsAppFloat } from "@/components/feature/WhatsAppPopup";
import { SidebarProvider, useSidebar } from "@/hooks/useSidebar";
import SiteSidebar from "@/components/feature/SiteSidebar";
import AuthCallback from "@/components/feature/AuthCallback";
import SiteStatusGuard from "@/components/feature/SiteStatusGuard";
import { useGoogleOAuthChoice } from "@/components/feature/GoogleOAuthChoice";
import { rememberBuyingPage } from "@/lib/authRedirect";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/feature/Navbar";
import LoggedOutNavbar from "@/components/feature/LoggedOutNavbar";
import ChatCorner from "@/components/feature/ChatCorner";
import BundleBar from "@/components/feature/BundleBar";
import NotifyPrompt from "@/components/feature/NotifyPrompt";
import MomoPayModal from "@/components/feature/MomoPayModal";
import MaintenanceNotice from "@/components/feature/MaintenanceNotice";

// Global error boundary to prevent blank pages on uncaught errors
class AppErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: string }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: "" };
  }

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, error: error instanceof Error ? error.message : "An unexpected error occurred" };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center bg-white border border-foreground-100 rounded-2xl p-8">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-4">
              <i className="ri-error-warning-line text-red-500 text-2xl"></i>
            </div>
            <h2 className="text-lg font-bold text-foreground-900 mb-2">Something went wrong</h2>
            <p className="text-sm text-foreground-500 mb-5">{this.state.error}</p>
            <button
              onClick={() => { this.setState({ hasError: false, error: "" }); window.location.reload(); }}
              className="bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-xl text-sm cursor-pointer transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function AppContent() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { isOpen: sidebarOpen, close: closeSidebar } = useSidebar();
  const { user } = useAuth();
  const isAdminPage = pathname.startsWith("/admin");
  const isRefLink = searchParams.has("ref");
  const isAuthPage = pathname === "/login" || pathname === "/register" || pathname === "/forgot-password";
  const isAppShellPage = pathname === "/dashboard";
  const isBuyPage = pathname === "/buy";
  const isLoginView = pathname === "/" && !user;
  const hidePromos = isAdminPage || isRefLink || isLoginView;
  const { ChoiceComponent } = useGoogleOAuthChoice(user?.id, user?.email, user?.name);
  const isLoggedOut = !user;
  const showLoggedOutNav = isLoggedOut;

  useEffect(() => {
    rememberBuyingPage(pathname, window.location.search);
  }, [pathname]);

  useEffect(() => {
    const now = Date.now();
    const lastActiveRaw = localStorage.getItem("mad_last_active");
    const keepSignedIn = localStorage.getItem("mad_keep_signed_in") !== "false";

    if (!keepSignedIn && lastActiveRaw) {
      const lastActive = parseInt(lastActiveRaw);
      const inactiveTime = now - lastActive;
      if (inactiveTime > 10 * 60 * 1000) {
        { const s = nhost.getUserSession(); if (s?.refreshToken) nhost.auth.signOut({ refreshToken: s.refreshToken }).catch(() => {}); nhost.clearSession(); }
      }
    }

    localStorage.setItem("mad_last_active", String(now));

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        localStorage.setItem("mad_last_active", String(Date.now()));
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  return (
    <SiteStatusGuard>
      <ScrollToTop />
      <RouteRedirects />
      <AuthCallback />
      <SiteSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      {!isAdminPage && !isAppShellPage && !isBuyPage && showLoggedOutNav ? <LoggedOutNavbar /> : !isAdminPage && !isAppShellPage && !isBuyPage && !isAuthPage ? <Navbar /> : null}
      <AppRoutes />
      {!hidePromos && <WhatsAppFloat />}
      {!isAdminPage && <ChatCorner />}
      {!isAdminPage && <BundleBar />}
      {!isAdminPage && <NotifyPrompt />}
      <MomoPayModal />
      {!isAdminPage && <MaintenanceNotice />}
      {ChoiceComponent}
    </SiteStatusGuard>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <SidebarProvider>
          <AppErrorBoundary>
            <AppContent />
          </AppErrorBoundary>
        </SidebarProvider>
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;